import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface SendUserInviteRequest {
  UserId: string;  // Backend expects PascalCase
  EmailAddress: string;  // Backend expects PascalCase
  AccessToken: string;  // JWT token for authentication
  RedirectUrl: string;  // URL to redirect after accepting the invite
  ApiBaseUrl?: string;  // Optional API base URL for tracking links (e.g., http://localhost:5000)
  ConfirmResend?: boolean;  // If true, confirms resending even if previous invitation was redeemed
}

export interface SendUserInviteResponse {
  success: boolean;
  message: string;
  invitationId?: string;
  sentAt?: string;
  requiresConfirmation?: boolean;
  previousRedeemedAt?: string;
  previousSentAt?: string;
}

export interface ResetTermsAcceptanceResponse {
  success: boolean;
  message: string;
  userId: string;
  hasAcceptedTerms: boolean;
}

export interface UpdateUserPermissionsRequest {
  userId: string;
  authorizations: {
    tasks: boolean;
    entities: boolean;
    documents: boolean;
    invoices: boolean;
    messages: boolean;
    shares: boolean;
    relatedParties: boolean;
    requests: boolean;
    banking: boolean;
    assets: boolean;
    investments: boolean;
    information: boolean;
    loans: boolean;
  };
}
export interface CreateUserRequest {
  emailAddress: string;
  displayName?: string;
  authorizations: {
    tasks: boolean;
    entities: boolean;
    documents: boolean;
    invoices: boolean;
    messages: boolean;
    shares: boolean;
    relatedParties: boolean;
    requests: boolean;
    banking: boolean;
    assets: boolean;
    investments: boolean;
    information: boolean;
    loans: boolean;
  };
  tenantId: string;
  siteInstanceKey: string;  
}

export interface User {
  id: string;
  emailAddress: string;
  name: string;
  displayName?: string;
  authorizations: {
    tasks: boolean;
    entities: boolean;
    documents: boolean;
    invoices: boolean;
    messages: boolean;
    shares: boolean;
    relatedParties: boolean;
    requests: boolean;
    banking: boolean;
    assets: boolean;
    investments: boolean;
    information: boolean;
    loans: boolean;
  };
  siteId: string;
  siteInstanceId: string;
  tenantId: string;
}

export interface CreateUserResponse {
  user: User;
  userExistedInEntraId: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/users';
  
  // Extract error message from HttpErrorResponse with nested property fallbacks
  private extractErrorMessage(error: HttpErrorResponse, defaultMessage: string): string {
    // Check for ApiErrorResponse format (both camelCase and PascalCase)
    // Azure Functions serialize with camelCase by default
    if (error.error?.details) {
      return error.error.details;
    }
    if (error.error?.Details) {
      return error.error.Details;
    }
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.error?.Message) {
      return error.error.Message;
    }
    // Check for { error: "message" } format (e.g., ConflictObjectResult)
    if (typeof error.error?.error === 'string') {
      return error.error.error;
    }
    // Fallback to other common error formats
    return error.error?.error?.error?.message 
      || error.error?.error?.message 
      || defaultMessage;
  }

  createUser(user: CreateUserRequest): Observable<CreateUserResponse> {
    return this.http.post<CreateUserResponse>(this.apiUrl, user).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(this.extractErrorMessage(error, 'Failed to create user')))
      )
    );
  }

  updateUserPermissions(request: UpdateUserPermissionsRequest): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${request.userId}/permissions`, request).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(this.extractErrorMessage(error, 'Failed to update user permissions')))
      )
    );
  }

  deleteUser(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${userId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        // For delete user, extract the Message property from ApiErrorResponse
        const message = error.error?.Message || error.error?.message || this.extractErrorMessage(error, 'Failed to delete user');
        return throwError(() => new Error(message));
      })
    );
  }

  sendUserInvite(request: SendUserInviteRequest, accessToken?: string): Observable<SendUserInviteResponse> {
    const token = accessToken || request.AccessToken;
    return this.http.post<SendUserInviteResponse>(`${this.apiUrl}/${request.UserId}/invite`, request, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(this.extractErrorMessage(error, 'Failed to send user invite')))
      )
    );
  }

  resetTermsAcceptance(userId: string): Observable<ResetTermsAcceptanceResponse> {
    return this.http.post<ResetTermsAcceptanceResponse>(
      `${this.apiUrl}/${userId}/reset-terms`,
      {}
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error, 'Failed to reset terms acceptance');
        return throwError(() => new Error(message));
      })
    );
  }

  updateUserDisplayName(userId: string, displayName: string | null): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${userId}`, { displayName }).pipe(
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error, 'Failed to update display name');
        return throwError(() => new Error(message));
      })
    );
  }

  updateUserStatus(email: string, status: number): Observable<User> {
    return this.http.post<User>(
      `${this.apiUrl}/status`,
      { email, status }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error, 'Failed to update user status');
        return throwError(() => new Error(message));
      })
    );
  }

}
